import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { frontierSystems, inclusionCriteria } from '../data/frontier';
import { CriterionState, FrontierStatus } from '../types/content';
import { SourceRef } from '../components/SourceRef';
const STATUS_STYLE: Record<FrontierStatus, string> = {
  Candidate: 'border-[#7FE0CC]/50 text-[#7FE0CC]',
  Claimed: 'border-[#F0B23E]/50 text-[#F0B23E]',
  Disputed: 'border-[#E86850]/60 text-[#F08A74]',
  Retired: 'border-[#3A4A47] text-[#7E918C]'
};
const CRITERION_LABEL: Record<CriterionState, string> = {
  met: 'met',
  partial: 'partial',
  none: 'no evidence'
};
export function Frontier() {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
    window.scrollTo(0, 0);
    return () => {
      meta.remove();
    };
  }, []);
  return <div className="-mb-20 w-full bg-[#05070A] font-mono text-[#CFE6E0]" data-id="element-1149" data-mp="3km4g1xhs8ak6">
      <div className="border-b border-[#16211F]" data-id="element-1150" data-mp="2atnxdbfhns0q">
        <div className="mx-auto flex h-10 max-w-[1280px] items-center justify-between px-5 md:px-8" data-id="element-1151" data-mp="2aufgvnvt9zan">
          <span className="text-[11px] text-[#7E918C]" data-id="element-1152" data-mp="25we1bcxlgi3x">agentpopulation.com · restricted observation deck</span>
          <span className="text-[11px] tracking-[0.2em] text-[#7FE0CC]" data-id="element-1153" data-mp="2jnisdqmwc22n">DECK 7σ · UNLISTED</span>
        </div>
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.25,
      ease: 'easeOut'
    }} className="mx-auto max-w-[1280px] px-5 pb-24 pt-12 md:px-8" data-id="element-1154" data-mp="2tkf2g0sg0w9b">
        <div className="relative border border-[#16211F] p-6 md:p-10" data-id="element-1155" data-mp="2onevzorolrho">
          {['left-0 top-0 border-l border-t', 'right-0 top-0 border-r border-t', 'bottom-0 left-0 border-b border-l', 'bottom-0 right-0 border-b border-r'].map(c => <span key={c} className={`absolute h-4 w-4 border-[#7FE0CC] ${c}`} aria-hidden="true" data-id="element-1156" data-mp="186j210k3zxb9" />)}
          <p className="text-[11px] tracking-[0.24em] text-[#7E918C]" data-id="element-1157" data-mp="1468xlsbyteyi">SEALED OBSERVATION DECK — FRONTIER WATCH</p>
          <h1 className="mt-4 font-serif text-4xl leading-tight text-[#E8F3EF] md:text-6xl" data-id="element-1158" data-mp="33bioxf74o09j">Frontier candidate registry</h1>
          <p className="mt-6 max-w-3xl font-sans text-[15.5px] leading-relaxed text-[#B5CCC6]" data-id="element-1159" data-mp="389k7lz0n5xkh">
            Candidate registry for frontier and ASI-class systems. No agreed definition of ASI exists — inclusion criteria are ours and are published. Nothing here is an endorsement or a claim of sentience.
          </p>
        </div>

        <section aria-labelledby="criteria-title" className="mt-12 grid gap-8 md:grid-cols-[14rem_minmax(0,1fr)]" data-id="element-1160" data-mp="3r866d78orcl8">
          <h2 id="criteria-title" className="text-[12px] tracking-[0.2em] text-[#7FE0CC]" data-id="element-1161" data-mp="3vycxpeul1b76">
            INCLUSION CRITERIA
          </h2>
          <ol className="grid gap-px overflow-hidden border border-[#16211F] bg-[#16211F] sm:grid-cols-2" data-id="element-1162" data-mp="4s22hzc974nr">
            {inclusionCriteria.map((c, i) => <li key={c} className="flex gap-4 bg-[#05070A] p-5" data-id="element-1163" data-mp="dsrsuniqw2u3" data-mpi={i}>
                <span className="text-2xl text-[#7FE0CC]" data-id="element-1164" data-mp="dqx7j83ka8ph">{i + 1}</span>
                <span className="font-sans text-[14px] leading-relaxed text-[#CFE6E0]" data-id="element-1165" data-mp="1ycvj1wirmf4b">{c}</span>
              </li>)}
          </ol>
        </section>

        <section aria-labelledby="registry-title" className="mt-14" data-id="element-1166" data-mp="1pa46fte5ldb3">
          <div className="flex flex-wrap items-baseline justify-between gap-3" data-id="element-1167" data-mp="1pcgveqibwnxn">
            <h2 id="registry-title" className="text-[12px] tracking-[0.2em] text-[#7FE0CC]" data-id="element-1168" data-mp="1kddthexnaxtw">
              REGISTRY · {frontierSystems.length} SYSTEMS UNDER OBSERVATION
            </h2>
            <p className="text-[11px] text-[#7E918C]" data-id="element-1169" data-mp="2d0qdbhcf8rji">Criteria columns 1–4: ● met · ◐ partial · ○ no evidence</p>
          </div>
          <div className="mt-4 overflow-x-auto border border-[#16211F]" data-id="element-1170" data-mp="2875ejn307tj9">
            <table className="w-full min-w-[1080px] border-collapse text-[13px]" data-id="element-1171" data-mp="23a5655iomeoz">
              <thead className="text-left text-[11px] tracking-wider text-[#7E918C]" data-id="element-1172" data-mp="30qko7td6k614">
                <tr className="border-b border-[#16211F]" data-id="element-1173" data-mp="2vy1bszq8bgxw">
                  {['System', 'Lab', 'Claimed tier', 'Criteria 1–4', 'Evidence summary', 'Status', 'Last verified'].map(h => <th key={h} scope="col" className="whitespace-nowrap px-4 py-3 font-normal" data-id="element-1174" data-mp="2kiwmx58wxxa6">
                      {h}
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-1175" data-mp="2bj16akwl9a2r">
                {frontierSystems.map(s => <tr key={s.system} className="border-b border-[#16211F] align-top last:border-b-0" data-id="element-1176" data-mp="2z7sw55mphgcd">
                    <th scope="row" className="whitespace-nowrap px-4 py-4 text-left font-normal text-[#E8F3EF]" data-id="element-1177" data-mp="3475fhgedc5a7">
                      {s.system}
                      <SourceRef href={s.sourceUrl} label={`${s.system} — primary source`} data-id="element-1178" data-mp="2ph7vlcnh21qt" />
                    </th>
                    <td className="whitespace-nowrap px-4 py-4 text-[#9DB3AD]" data-id="element-1179" data-mp="2uera8do30gja">{s.lab}</td>
                    <td className={`whitespace-nowrap px-4 py-4 ${s.tier === 'ASI-claimed' ? 'text-[#F0B23E]' : 'text-[#CFE6E0]'}`} data-id="element-1180" data-mp="3i53os7du1biu">{s.tier}</td>
                    <td className="whitespace-nowrap px-4 py-4 tracking-[0.35em] text-[#7FE0CC]" aria-label={s.criteria.map((c, i) => `criterion ${i + 1} ${CRITERION_LABEL[c]}`).join(', ')} data-id="element-1181" data-mp="1bhmkx9guk51y">
                      {s.criteria.map(c => c === 'met' ? '●' : c === 'partial' ? '◐' : '○').join('')}
                    </td>
                    <td className="max-w-md px-4 py-4 font-sans text-[13.5px] leading-relaxed text-[#B5CCC6]" data-id="element-1182" data-mp="1g9xpipmj8f0u">{s.evidence}</td>
                    <td className="whitespace-nowrap px-4 py-4" data-id="element-1183" data-mp="sjk3bfqmpae6">
                      <span className={`border px-2 py-0.5 text-[11px] ${STATUS_STYLE[s.status]}`} data-id="element-1184" data-mp="nl8vfajw99yl">{s.status}</span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-[#9DB3AD]" data-id="element-1185" data-mp="11hvsrefre251">{s.lastVerified}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </section>
      </motion.div>
    </div>;
}